import {ChangeDetectionStrategy, Component} from '@angular/core'
import {Language} from '../../../i18n/language'
import {TranslocoModule, TranslocoService} from '@ngneat/transloco'
import {ConfigService} from '../../../config/config.service'
import {getSeoUrl} from '../../../seo/util'
import {DirectivesModule} from '../../../../shared/directives/directives.module'

@Component({
  selector: 'bgo-footer-seo-keywords',
  templateUrl: './seo-keywords.component.html',
  styleUrls: ['./seo-keywords.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [DirectivesModule, TranslocoModule],
})
export class SeoKeywordsComponent {
  private readonly keywordPairs = new Map<Language, KeywordPair[]>([
    [Language.GERMAN, [...germanKeywords]],
    [Language.FRENCH, [...frenchKeywords]],
    [Language.ENGLISH, [...englishKeywords]],
    [Language.ITALIAN, [...italianKeywords]],
  ])
  readonly keywords: {text: string; url: string}[]

  constructor(
    readonly localeService: TranslocoService,
    private readonly configService: ConfigService,
  ) {
    const lang = this.localeService.getActiveLang()
    const pair = this.keywordPairs.get(lang as Language) ?? []
    this.keywords = pair.map(pair => ({
      text: pair.text,
      url: this.buildUrl(pair.pathname),
    }))
  }

  private buildUrl(path: string): string {
    const lang = this.localeService.getActiveLang()
    return getSeoUrl(this.configService.config, `/${lang}${path}`)
  }
}

interface KeywordPair {
  text: string
  // the pathname must not include the language
  pathname: string
}

const keyword = (text: string, pathname: string): KeywordPair => ({text, pathname})

const germanKeywords: KeywordPair[] = [
  keyword('Handwerker gesucht', '/'),
  keyword('Maler Zürich', '/craftsmen/zurich/painter'),
  keyword('Elektriker Zürich', '/craftsmen/zurich/electrician'),
  keyword('Bodenleger Zürich', '/craftsmen/zurich/flooring'),
  keyword('Garage Zürich', '/craftsmen/zurich/car-mechanic'),
  keyword('Maler Bern', '/craftsmen/bern/painter'),
  keyword('Elektriker Bern', '/craftsmen/bern/electrician'),
  keyword('Bodenleger Bern', '/craftsmen/bern/flooring'),
  keyword('Garage Bern', '/craftsmen/bern/car-mechanic'),
  keyword('Maler Basel', '/craftsmen/basel/painter'),
  keyword('Elektriker Basel', '/craftsmen/basel/electrician'),
  keyword('Bodenleger Basel', '/craftsmen/basel/flooring'),
  keyword('Garage Basel', '/craftsmen/basel/car-mechanic'),
  keyword('Photovoltaik-Anlage', '/services/photovoltaic-system'),
  keyword('Wärmepumpe', '/services/heat-pumps'),
  keyword('Haushaltgeräte reparieren', '/industries/appliance-repair'),
  keyword('E-Ladestation', '/services/e-vehicles-chargers'),
  keyword('Rohrleitung reinigen', '/services/plumbing-repair'),
  keyword('Parkett pflegen', '/services/parquet-maintenance'),
  keyword('Katzenklappe installieren', '/services/cat-door-installation'),
  keyword('Gartenpflege', '/services/care-and-maintenance'),
  keyword('Gartenzaun', '/services/fences-and-gates'),
]
const frenchKeywords: KeywordPair[] = [
  keyword('Trouver un artisan', '/'),
  keyword('Peintre Lausanne', '/craftsmen/lausanne/painter'),
  keyword('Électricien Lausanne', '/craftsmen/lausanne/electrician'),
  keyword('Parqueteur Lausanne', '/craftsmen/lausanne/flooring'),
  keyword('Garage Lausanne', '/craftsmen/lausanne/car-mechanic'),
  keyword('Peintre Genève', '/craftsmen/geneve/painter'),
  keyword('Électricien Genève', '/craftsmen/geneve/electrician'),
  keyword('Parqueteur Genève', '/craftsmen/geneve/flooring'),
  keyword('Garage Genève', '/craftsmen/geneve/car-mechanic'),
  keyword('Peintre Neuchâtel', '/craftsmen/neuchatel/painter'),
  keyword('Électricien Neuchâtel', '/craftsmen/neuchatel/electrician'),
  keyword('Parqueteur Neuchâtel', '/craftsmen/neuchatel/flooring'),
  keyword('Garage Neuchâtel', '/craftsmen/neuchatel/car-mechanic'),
  keyword('Panneaux photovoltaïques', '/services/photovoltaic-system'),
  keyword('Pompes à chaleur', '/services/heat-pumps'),
  keyword('Réparation électroménager', '/industries/appliance-repair'),
  keyword('Bornes véhicules électriques', '/services/e-vehicles-chargers'),
  keyword('Nettoyage plomberie', '/services/plumbing-repair'),
  keyword('Entretien de parquet', '/services/parquet-maintenance'),
  keyword('Installation de chatières', '/services/cat-door-installation'),
  keyword('Entretien de jardin', '/services/care-and-maintenance'),
  keyword('Clôtures et portails', '/services/fences-and-gates'),
]
const englishKeywords: KeywordPair[] = []
const italianKeywords: KeywordPair[] = []
